/**
 * Organization URL utilities
 * Handles proper extraction of organization IDs from URLs
 */

/**
 * Extract organization ID from URL path
 * Handles both full UUIDs and truncated IDs
 */
export const extractOrgIdFromPath = (pathname: string): string | null => {
  try {
    // Pattern 1: /o/{full-uuid} - Standard organization route
    const fullUuidPattern = /\/o\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/;
    const fullUuidMatch = pathname.match(fullUuidPattern);
    if (fullUuidMatch) {
      console.log('✅ Extracted full UUID orgId:', fullUuidMatch[1]);
      return fullUuidMatch[1];
    }

    // Pattern 2: /o/{truncated-id} - Handle truncated UUIDs
    const truncatedPattern = /\/o\/([a-f0-9]{8})/;
    const truncatedMatch = pathname.match(truncatedPattern);
    if (truncatedMatch) {
      console.log('⚠️ Found truncated orgId:', truncatedMatch[1]);
      return truncatedMatch[1];
    }

    // Pattern 3: /o/{any-id} - Fallback for any organization ID
    const fallbackPattern = /\/o\/([^\/]+)/;
    const fallbackMatch = pathname.match(fallbackPattern);
    if (fallbackMatch) {
      console.log('🔍 Found fallback orgId:', fallbackMatch[1]);
      return fallbackMatch[1];
    }

    console.log('❌ No organization ID found in path:', pathname);
    return null;
  } catch (error) {
    console.error('❌ Error extracting organization ID:', error);
    return null;
  }
};

/**
 * Validate if an organization ID is a valid UUID
 */
export const isValidOrgId = (orgId: string): boolean => {
  const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  return uuidPattern.test(orgId);
};

/**
 * Get organization ID from current URL
 */
export const getCurrentOrgId = (): string | null => {
  return extractOrgIdFromPath(window.location.pathname);
};

/**
 * Build organization URL with proper ID
 */
export const buildOrgUrl = (orgId: string, path: string = ''): string => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `/o/${orgId}${cleanPath}`;
};

/**
 * Debug organization ID extraction
 */
export const debugOrgIdExtraction = (pathname: string): void => {
  console.log('🔍 Debugging organization ID extraction:');
  console.log('📍 Pathname:', pathname);
  
  const orgId = extractOrgIdFromPath(pathname);
  console.log('🆔 Extracted orgId:', orgId);
  
  if (orgId) {
    console.log('✅ Valid UUID:', isValidOrgId(orgId));
    console.log('📏 Length:', orgId.length);
  }
};
